import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useMessages } from '../context/MessagesContext';
import NotificationsDropdown from './NotificationsDropdown';
import {
  LogOut,
  User,
  LayoutDashboard,
  Heart,
  MessageSquare,
  Menu,
  X
} from 'lucide-react';

const Navbar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { favoritesCount } = useFavorites();
  const { unreadCount } = useMessages();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.jpg" 
              alt="Alliance Solidarité" 
              className="h-10 w-10 rounded-lg shadow-md group-hover:shadow-lg transition-shadow"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Alliance Solidarité
            </span>
          </Link>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {isAuthenticated && user && <NotificationsDropdown />}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/carte" 
              className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors font-medium"
            >
              🗺️ Carte Interactive
            </Link>
            
            {isAuthenticated && (
              <>
                <Link 
                  to="/favoris" 
                  className="relative flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors font-medium"
                >
                  <Heart className="w-5 h-5" />
                  Favoris
                  {favoritesCount > 0 && (
                    <Badge variant="destructive" className="absolute -top-2 -right-3 h-5 w-5 flex items-center justify-center p-0 text-xs">
                      {favoritesCount}
                    </Badge>
                  )}
                </Link>
                <Link 
                  to="/ajouter" 
                  className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors font-medium"
                >
                  ➕ Ajouter une Offre
                </Link>
                <Link 
                  to="/messages" 
                  className="relative flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  <MessageSquare className="w-5 h-5" />
                  Messages
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-2 -right-3 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-600">
                      {unreadCount}
                    </Badge>
                  )}
                </Link>
              </>
            )}

            {isAuthenticated && user ? (
              <div className="flex items-center gap-3">
                {/* Notifications */}
                <NotificationsDropdown />
                
                {/* User Menu */}
                <div ref={userMenuRef} className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.profile_picture_url || user.avatar} alt={user.name} />
                    <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                      {user.name?.charAt(0) || user.email?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden lg:inline font-medium">{user.name}</span>
                  <svg 
                    className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                      <p className="text-xs text-emerald-600 font-semibold mt-1 capitalize">{user.role}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/profil');
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Mon Profil
                    </button>
                    
                    {['owner', 'admin', 'moderator'].includes(user.role) && (
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/dashboard');
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Tableau de Bord
                      </button>
                    )}
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
              </div>
            ) : (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  Connexion
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/carte" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                🗺️ Carte Interactive
              </Link>
              
              {isAuthenticated && (
                <>
                  <Link 
                    to="/favoris" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Heart className="w-5 h-5" />
                    Favoris
                    {favoritesCount > 0 && (
                      <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs">
                        {favoritesCount}
                      </Badge>
                    )}
                  </Link>
                  <Link 
                    to="/ajouter" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    ➕ Ajouter une Offre
                  </Link>
                  <Link 
                    to="/messages" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Messages
                    {unreadCount > 0 && (
                      <Badge className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                        {unreadCount}
                      </Badge>
                    )}
                  </Link>
                  
                  <div className="border-t border-gray-200 my-2"></div>
                  
                  {user && (
                    <>
                      <div className="px-4 py-2 bg-gray-50 rounded-md">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <p className="text-xs text-emerald-600 font-semibold mt-1 capitalize">{user.role}</p>
                      </div>
                      
                      <Link
                        to="/profil"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <User className="w-5 h-5" />
                        Mon Profil
                      </Link>
                      
                      {['owner', 'admin', 'moderator'].includes(user.role) && (
                        <Link
                          to="/dashboard"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <LayoutDashboard className="w-5 h-5" />
                          Tableau de Bord
                        </Link>
                      )}
                      
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors w-full text-left"
                      >
                        <LogOut className="w-5 h-5" />
                        Déconnexion
                      </button>
                    </>
                  )}
                </>
              )}
              
              {!isAuthenticated && (
                <Link 
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4"
                >
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    Connexion
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
